import React from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

/**
 * Story DNA Panel — surfaces extracted canon BEFORE generation begins.
 * Lets the user verify characters/events/ending and lock critical items
 * as immutable 🔒 canon (P0 #1 + #2 from PRD).
 */
function LockToggle({ active, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`lock-toggle-${label.slice(0, 20).replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
      className={`text-xs px-2 py-0.5 rounded-md border transition-colors ${
        active
          ? "bg-amber-500/20 border-amber-500/50 text-amber-200"
          : "bg-zinc-800/40 border-zinc-700 text-zinc-400 hover:border-zinc-500"
      }`}
      title={active ? "Locked — will appear verbatim in your story" : "Click to lock this item"}
    >
      {active ? "🔒 Locked" : "Lock"}
    </button>
  );
}

export default function StoryDnaPanel({ canon, locks, setLocks, onConfirm, onCancel }) {
  if (!canon) return null;

  const toggleLock = (key) => {
    const next = new Set(locks);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setLocks(next);
  };

  const characters = canon.characters || [];
  const events = canon.required_events || [];
  const phrases = canon.exact_phrases || [];
  const forbidden = canon.forbidden_changes || [];
  const setting = canon.setting || "";
  const ending = (canon.required_ending && canon.required_ending !== "open") ? canon.required_ending : null;

  return (
    <div className="space-y-5 py-4" data-testid="story-dna-panel">
      <div className="flex items-start gap-3">
        <div className="text-3xl">🧬</div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-white">Story DNA</h2>
          <p className="text-sm text-zinc-400 mt-1">
            Verify what was extracted from your idea. Click <span className="text-amber-300">🔒 Lock</span> on
            anything critical — locked items become absolute law and cannot be renamed, replaced, or removed.
          </p>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline" className="text-zinc-300">{characters.length} characters</Badge>
        <Badge variant="outline" className="text-zinc-300">{events.length} required events</Badge>
        <Badge variant="outline" className="text-zinc-300">{phrases.length} exact phrases</Badge>
        <Badge variant="outline" className="text-zinc-300">{forbidden.length} forbidden changes</Badge>
        {ending && <Badge variant="outline" className="text-emerald-300">Ending identified</Badge>}
      </div>

      {/* Title + Logline */}
      {(canon.title || canon.logline) && (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4 space-y-2">
          {canon.title && <div className="text-lg text-white font-medium">{canon.title}</div>}
          {canon.logline && <div className="text-sm text-zinc-300 italic">{canon.logline}</div>}
        </div>
      )}

      {/* Characters */}
      {characters.length > 0 && (
        <Section title="Characters">
          <ul className="space-y-2">
            {characters.map((c, i) => (
              <li key={i} className="flex items-center justify-between gap-3 text-sm">
                <div>
                  <span className="text-white">{c.name}</span>
                  {c.role && <span className="text-zinc-500 ml-2">({c.role})</span>}
                  {c.description && <span className="text-zinc-400 ml-2">— {c.description}</span>}
                </div>
                <LockToggle active={locks.has(c.name)} onClick={() => toggleLock(c.name)} label={c.name} />
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Setting */}
      {setting && (
        <Section title="Setting">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-zinc-300">{setting}</span>
            <LockToggle active={locks.has(setting)} onClick={() => toggleLock(setting)} label="setting" />
          </div>
        </Section>
      )}

      {/* Required Events */}
      {events.length > 0 && (
        <Section title="Required Events">
          <ol className="list-decimal list-inside space-y-1 text-sm text-zinc-300">
            {events.map((e, i) => <li key={i}>{e}</li>)}
          </ol>
        </Section>
      )}

      {/* Required Ending */}
      {ending && (
        <Section title="Required Ending">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-emerald-300">{ending}</span>
            <LockToggle
              active={locks.has(`ENDING: ${ending}`)}
              onClick={() => toggleLock(`ENDING: ${ending}`)}
              label="ending"
            />
          </div>
        </Section>
      )}

      {/* Exact Phrases */}
      {phrases.length > 0 && (
        <Section title="Exact Phrases (preserved verbatim)">
          <ul className="space-y-1 text-sm">
            {phrases.map((p, i) => {
              const key = `PHRASE: "${p}"`;
              return (
                <li key={i} className="flex items-center justify-between gap-3">
                  <span className="text-zinc-300 italic">&ldquo;{p}&rdquo;</span>
                  <LockToggle active={locks.has(key)} onClick={() => toggleLock(key)} label={`phrase-${i}`} />
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {/* Forbidden */}
      {forbidden.length > 0 && (
        <Section title="Forbidden Changes">
          <ul className="space-y-1 text-sm">
            {forbidden.map((f, i) => {
              const key = `FORBIDDEN: ${f}`;
              return (
                <li key={i} className="flex items-center justify-between gap-3">
                  <span className="text-rose-300">⛔ {f}</span>
                  <LockToggle active={locks.has(key)} onClick={() => toggleLock(key)} label={`forbid-${i}`} />
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      <div className="text-xs text-zinc-500 pt-2">
        {locks.size} item{locks.size === 1 ? "" : "s"} locked. You can edit locks on the project page after
        generation too.
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-3 border-t border-zinc-800">
        <Button
          variant="ghost"
          onClick={onCancel}
          data-testid="dna-cancel-btn"
          className="text-zinc-400 hover:text-zinc-200"
        >
          ← Edit my idea
        </Button>
        <Button
          onClick={onConfirm}
          data-testid="dna-confirm-btn"
          className="w-full sm:w-auto h-12 px-6 text-base font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 shadow-lg shadow-emerald-500/40 ring-2 ring-emerald-400/60 animate-pulse hover:animate-none"
        >
          ✨ Write My Story Now →
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-zinc-500">{title}</div>
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">{children}</div>
    </div>
  );
}
